import React, { useState, useEffect, createContext, useContext } from 'react';
import { IntlProvider } from 'react-intl';

// 1. 定义支持的语言类型
type Locale = 'en-US' | 'zh-CN' | 'th-TH';

// 2. 创建 Context，用于让子组件能调用 setLocale 切换语言
interface LanguageContextType {
  locale: Locale;
  switchLanguage: (lang: Locale) => void;
}

export const LanguageContext = createContext<LanguageContextType>({
  locale: 'zh-CN',
  switchLanguage: () => {},
});

// 3. 导出 Hook，方便组件使用
export const useLanguage = () => useContext(LanguageContext);

// 4. Provider 组件
export const LanguageProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  // 从 localStorage 读取语言设置，如果不存在或无效则使用默认值 'zh-CN'
  const getInitialLocale = (): Locale => {
    const savedLocale = localStorage.getItem('locale');
    const validLocales: Locale[] = ['en-US', 'zh-CN', 'th-TH'];
    
    if (savedLocale && validLocales.includes(savedLocale as Locale)) {
      return savedLocale as Locale;
    }
    
    return 'zh-CN';
  };

  const [locale, setLocale] = useState<Locale>(getInitialLocale());
  const [messages, setMessages] = useState<Record<string, string> | null>(null);

  // 核心逻辑：监听 locale 变化，动态加载对应的 JSON 文件
  useEffect(() => {
    const loadMessages = async () => {
      try {
        const module = await import(`./locales/${locale}.ts`);
        setMessages(module.default);
      } catch (error) {
        console.error(`Cannot load locale: ${locale}`, error);
      }
    };

    loadMessages();
  }, [locale]);

  const switchLanguage = (lang: Locale) => {
    setLocale(lang);
    // 将语言设置保存到 localStorage
    localStorage.setItem('locale', lang);
  };

  // 如果消息还没加载完，显示 Loading（防止页面闪烁）
  if (!messages) {
    return null;
  }

  return (
    <LanguageContext.Provider value={{ locale, switchLanguage }}>
      <IntlProvider
        locale={locale}
        messages={messages}
        defaultLocale="en"
      >
        {children}
      </IntlProvider>
    </LanguageContext.Provider>
  );
};